import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  Cpu,
  Eye,
  KeyRound,
  Loader2,
  Pencil,
  RefreshCcw,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  activateOnPremLlmRuntimeDraft,
  createOnPremLlmRuntimeDraft,
  deleteOnPremLlmRuntimeDraft,
  deleteOnPremLlmRuntimeProfile,
  getOnPremErrorMessage,
  getOnPremLlmRuntimeActive,
  getOnPremLlmRuntimeOptions,
  getOnPremLlmRuntimeProfiles,
  getOnPremLocalLlmServiceModels,
  getOnPremLocalLlmServices,
  patchOnPremLlmRuntimeDraft,
  replaceOnPremLlmRuntimeCredential,
} from "../../apiOnPremAdmin";
import {
  buildCredentialPayload,
  buildDraftPayload,
  getProviderLabel,
  getRuntimeMode,
  profileToCredential,
  profileToDraft,
  validateCredentialDraft,
  validateRuntimeDraft,
} from "./llmRuntimeState";
import "./OnPremAdmin.css";

const emptyOptions = { hosted_providers: [], local_providers: [] };
const emptyLocalModels = { primary_models: [], fast_models: [] };
const emptyCredential = { displayName: "", apiKey: "" };

const createEmptyDraft = () => ({
  mode: "hosted",
  provider: "groq",
  serviceType: "vllm",
  baseUrl: "",
  primaryModel: "",
  fastModel: "",
  requestTimeoutSeconds: "60",
  maxRetries: "2",
});

function FieldError({ children }) {
  return children ? <small className="onprem-field-error">{children}</small> : null;
}

function Required() {
  return <span className="onprem-required" aria-hidden="true">*</span>;
}

function isLocalProfile(profile) {
  return getRuntimeMode(profile) === "local";
}

function getProfileModels(profile) {
  return [profile?.primary_model, profile?.fast_model].filter(Boolean).join(" / ") || "Backend defaults";
}

function getCredentialLabel(profile) {
  if (!profile?.credential?.configured) return "Not configured";
  return profile.credential.display_name || profile.credential.api_key || "Configured";
}

function RuntimeStatus({ active }) {
  if (!active) {
    return (
      <div className="onprem-inline-warning onprem-runtime-status">
        <AlertCircle size={18} />
        <div>
          <strong>No runtime is active</strong>
          <p>Create a draft and activate it before completing onboarding.</p>
        </div>
      </div>
    );
  }

  const mode = getRuntimeMode(active);
  return (
    <div className="onprem-runtime-current">
      <h3>Currently active config</h3>
      <div className="onprem-runtime-summary">
        <div>
          <span>Provider</span>
          <strong>{getProviderLabel(active.provider, active.server_type)}</strong>
        </div>
        <div>
          <span>Mode</span>
          <strong>{mode === "local" ? "Local deployment" : "Hosted service"}</strong>
        </div>
        <div>
          <span>Models</span>
          <strong>{getProfileModels(active)}</strong>
        </div>
        <div>
          <span>Credential</span>
          <strong>{getCredentialLabel(active)}</strong>
        </div>
      </div>
    </div>
  );
}

function RuntimeFields({
  draft,
  credential,
  errors,
  credentialErrors,
  options,
  localServices,
  localModels,
  localModelsStatus,
  onDraftChange,
  onCredentialChange,
  onModeChange,
  idPrefix = "runtime",
  disabled = false,
  showCredential = true,
}) {
  const hostedProviders = options.hosted_providers || [];
  const selectedHostedProvider = hostedProviders.find(
    (provider) => provider.provider === draft.provider
  );
  const localProviderOptions = localServices.length
    ? localServices
    : (options.local_providers || []);

  return (
    <>
      <div className="onprem-tabs onprem-runtime-mode" role="group" aria-label="Runtime source">
        <button type="button" className={draft.mode === "hosted" ? "is-active" : ""} onClick={() => onModeChange("hosted")} disabled={disabled}>
          Hosted service
        </button>
        <button type="button" className={draft.mode === "local" ? "is-active" : ""} onClick={() => onModeChange("local")} disabled={disabled}>
          Local deployment
        </button>
      </div>

      <div className="onprem-form-grid">
        {draft.mode === "hosted" ? (
          <>
            <label className="onprem-field is-wide" htmlFor={`${idPrefix}-hosted-provider`}>
              <span>Hosted provider<Required /></span>
              <select id={`${idPrefix}-hosted-provider`} aria-label="Hosted provider" value={draft.provider} onChange={onDraftChange("provider")} disabled={disabled}>
                {hostedProviders.map((provider) => (
                  <option key={provider.provider} value={provider.provider}>
                    {getProviderLabel(provider.provider)}
                  </option>
                ))}
              </select>
              <FieldError>{errors.provider}</FieldError>
            </label>
            <div className="onprem-runtime-defaults is-wide">
              <span>Default models</span>
              <strong>{selectedHostedProvider?.default_models?.primary || "Not available"}</strong>
              <code>{selectedHostedProvider?.default_models?.fast || "No fast model"}</code>
            </div>
          </>
        ) : (
          <>
            <label className="onprem-field is-wide" htmlFor={`${idPrefix}-local-service`}>
              <span>Local service<Required /></span>
              <select id={`${idPrefix}-local-service`} aria-label="Local service" value={draft.serviceType} onChange={onDraftChange("serviceType")} disabled={disabled}>
                {localProviderOptions.map((service) => (
                  <option key={service.service_type || service.server_type || service.provider} value={service.service_type || service.server_type || "vllm"}>
                    {service.label || getProviderLabel(service.provider, service.server_type)}
                  </option>
                ))}
                <option value="ollama" disabled>Ollama - Coming soon</option>
              </select>
            </label>
            <label className="onprem-field is-wide" htmlFor={`${idPrefix}-base-url`}>
              <span>Base URL<Required /></span>
              <input id={`${idPrefix}-base-url`} aria-label="Base URL" value={draft.baseUrl} onChange={onDraftChange("baseUrl")} placeholder="http://localhost:8001/v1" disabled={disabled} />
              <FieldError>{errors.baseUrl}</FieldError>
            </label>
            <label className="onprem-field" htmlFor={`${idPrefix}-primary-model`}>
              <span>Primary model<Required /></span>
              <select id={`${idPrefix}-primary-model`} aria-label="Primary model" value={draft.primaryModel} onChange={onDraftChange("primaryModel")} disabled={disabled || localModelsStatus === "loading"}>
                {localModels.primary_models.map((model) => (
                  <option key={model.id} value={model.id}>{model.label}</option>
                ))}
              </select>
              <FieldError>{errors.primaryModel}</FieldError>
            </label>
            <label className="onprem-field" htmlFor={`${idPrefix}-fast-model`}>
              <span>Fast model<Required /></span>
              <select id={`${idPrefix}-fast-model`} aria-label="Fast model" value={draft.fastModel} onChange={onDraftChange("fastModel")} disabled={disabled || localModelsStatus === "loading"}>
                {localModels.fast_models.map((model) => (
                  <option key={model.id} value={model.id}>{model.label}</option>
                ))}
              </select>
              <FieldError>{errors.fastModel}</FieldError>
            </label>
          </>
        )}

        <label className="onprem-field" htmlFor={`${idPrefix}-request-timeout`}>
          <span>Request timeout seconds</span>
          <input id={`${idPrefix}-request-timeout`} inputMode="decimal" value={draft.requestTimeoutSeconds} onChange={onDraftChange("requestTimeoutSeconds")} placeholder="60" disabled={disabled} />
          <FieldError>{errors.requestTimeoutSeconds}</FieldError>
        </label>
        <label className="onprem-field" htmlFor={`${idPrefix}-max-retries`}>
          <span>Max retries</span>
          <input id={`${idPrefix}-max-retries`} inputMode="numeric" value={draft.maxRetries} onChange={onDraftChange("maxRetries")} placeholder="2" disabled={disabled} />
          <FieldError>{errors.maxRetries}</FieldError>
        </label>

        {showCredential ? (
          <>
            <label className="onprem-field" htmlFor={`${idPrefix}-credential-display-name`}>
              <span>Credential display name</span>
              <input id={`${idPrefix}-credential-display-name`} aria-label="Credential display name" value={credential.displayName} onChange={onCredentialChange("displayName")} placeholder="Production provider key" />
            </label>
            <label className="onprem-field" htmlFor={`${idPrefix}-api-key`}>
              <span>API key{draft.mode === "hosted" ? <Required /> : null}</span>
              <input id={`${idPrefix}-api-key`} aria-label="API key" type="password" value={credential.apiKey} onChange={onCredentialChange("apiKey")} placeholder={draft.mode === "local" ? "Optional local API key" : "Enter API key"} />
              <FieldError>{credentialErrors.apiKey}</FieldError>
            </label>
          </>
        ) : null}
      </div>
    </>
  );
}

function ProfileDetails({ profile }) {
  const mode = getRuntimeMode(profile);
  return (
    <div className="onprem-profile-details">
      <div><span>Status</span><strong>{profile.status || "unknown"}</strong></div>
      <div><span>Provider</span><strong>{getProviderLabel(profile.provider, profile.server_type)}</strong></div>
      <div><span>Mode</span><strong>{mode === "local" ? "Local deployment" : "Hosted service"}</strong></div>
      <div><span>Version</span><strong>{profile.version ?? "Not available"}</strong></div>
      <div><span>Primary model</span><strong>{profile.primary_model || "Backend default"}</strong></div>
      <div><span>Fast model</span><strong>{profile.fast_model || "Backend default"}</strong></div>
      <div><span>Base URL</span><strong>{profile.base_url || "Not used"}</strong></div>
      <div><span>Timeout</span><strong>{profile.request_timeout_seconds ?? "Default"}</strong></div>
      <div><span>Retries</span><strong>{profile.max_retries ?? "Default"}</strong></div>
      <div><span>Credential</span><strong>{getCredentialLabel(profile)}</strong></div>
    </div>
  );
}

function RuntimeDialog({ title, description, children, actions, onClose }) {
  return (
    <div className="onprem-dialog-backdrop" role="presentation">
      <div className="onprem-dialog onprem-runtime-dialog" role="dialog" aria-modal="true" aria-labelledby="runtime-dialog-title">
        <div className="onprem-dialog-head">
          <div>
            <h2 id="runtime-dialog-title">{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <button type="button" className="onprem-icon-button" onClick={onClose} aria-label="Close dialog">
            <X size={17} />
          </button>
        </div>
        {children}
        <div className="onprem-dialog-actions">{actions}</div>
      </div>
    </div>
  );
}

export default function OnPremLlmRuntimePage({
  heading = "Runtime configuration",
  description = "Create, activate, and maintain the runtime used by query jobs.",
  headerMeta = null,
  footer = null,
  onRuntimeChange = null,
}) {
  const [state, setState] = useState({
    status: "loading",
    message: "",
    options: emptyOptions,
    active: null,
    profiles: [],
    localServices: [],
  });
  const [localModels, setLocalModels] = useState(emptyLocalModels);
  const [localModelsStatus, setLocalModelsStatus] = useState("idle");
  const [busy, setBusy] = useState("");
  const [errors, setErrors] = useState({});
  const [credentialErrors, setCredentialErrors] = useState({});
  const [draft, setDraft] = useState(createEmptyDraft);
  const [credential, setCredential] = useState(emptyCredential);
  const [dialog, setDialog] = useState(null);
  const [editDraft, setEditDraft] = useState(createEmptyDraft);
  const [editCredential, setEditCredential] = useState(emptyCredential);
  const [editErrors, setEditErrors] = useState({});
  const [editCredentialErrors, setEditCredentialErrors] = useState({});
  const [rotateCredential, setRotateCredential] = useState(emptyCredential);
  const [rotateErrors, setRotateErrors] = useState({});

  useEffect(() => {
    onRuntimeChange?.({ draft, credential, active: state.active });
  }, [credential, draft, onRuntimeChange, state.active]);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const [optionsResponse, activeResponse, profilesResponse, servicesResponse] = await Promise.all([
        getOnPremLlmRuntimeOptions(),
        getOnPremLlmRuntimeActive(),
        getOnPremLlmRuntimeProfiles(),
        getOnPremLocalLlmServices(),
      ]);
      const nextOptions = optionsResponse.data || emptyOptions;
      setState({
        status: "ready",
        message: "",
        options: nextOptions,
        active: activeResponse.data?.active || null,
        profiles: profilesResponse.data?.profiles || [],
        localServices: servicesResponse.data?.services || nextOptions.local_providers || [],
      });
      setDraft((current) => ({
        ...current,
        provider: current.provider || nextOptions.hosted_providers?.[0]?.provider || "groq",
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: getOnPremErrorMessage(error, "LLM runtime configuration could not be loaded."),
      }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadLocalModels = useCallback((mode, serviceType, applyDefaults) => {
    let cancelled = false;
    if (mode !== "local" || !serviceType) return () => {};
    setLocalModelsStatus("loading");
    getOnPremLocalLlmServiceModels(serviceType)
      .then(({ data }) => {
        if (cancelled) return;
        const nextModels = data || emptyLocalModels;
        setLocalModels(nextModels);
        setLocalModelsStatus("ready");
        applyDefaults(nextModels);
      })
      .catch(() => {
        if (!cancelled) setLocalModelsStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadLocalModels(draft.mode, draft.serviceType, (nextModels) => {
    setDraft((current) => ({
      ...current,
      primaryModel: current.primaryModel || nextModels.primary_models?.[0]?.id || "",
      fastModel: current.fastModel || nextModels.fast_models?.[0]?.id || "",
    }));
  }), [draft.mode, draft.serviceType, loadLocalModels]);

  useEffect(() => loadLocalModels(editDraft.mode, editDraft.serviceType, (nextModels) => {
    setEditDraft((current) => ({
      ...current,
      primaryModel: current.primaryModel || nextModels.primary_models?.[0]?.id || "",
      fastModel: current.fastModel || nextModels.fast_models?.[0]?.id || "",
    }));
  }), [editDraft.mode, editDraft.serviceType, loadLocalModels]);

  const setMode = (mode) => {
    setErrors({});
    setCredentialErrors({});
    setDraft((current) => ({
      ...current,
      mode,
      requestTimeoutSeconds: mode === "local" ? "120" : "60",
      maxRetries: "2",
    }));
  };

  const setEditMode = (mode) => {
    setEditErrors({});
    setEditCredentialErrors({});
    setEditDraft((current) => ({
      ...current,
      mode,
      requestTimeoutSeconds: mode === "local" ? "120" : "60",
      maxRetries: "2",
    }));
  };

  const updateDraft = (field) => (event) => {
    setDraft((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };
  const updateCredential = (field) => (event) => {
    setCredential((current) => ({ ...current, [field]: event.target.value }));
    setCredentialErrors((current) => ({ ...current, [field]: undefined }));
  };
  const updateEditDraft = (field) => (event) => {
    setEditDraft((current) => ({ ...current, [field]: event.target.value }));
    setEditErrors((current) => ({ ...current, [field]: undefined }));
  };
  const updateEditCredential = (field) => (event) => {
    setEditCredential((current) => ({ ...current, [field]: event.target.value }));
    setEditCredentialErrors((current) => ({ ...current, [field]: undefined }));
  };
  const updateRotateCredential = (field) => (event) => {
    setRotateCredential((current) => ({ ...current, [field]: event.target.value }));
    setRotateErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validateForm = (nextDraft, nextCredential, requireHostedKey) => {
    const nextErrors = validateRuntimeDraft(nextDraft);
    const nextCredentialErrors =
      requireHostedKey && nextDraft.mode === "hosted"
        ? validateCredentialDraft({ ...nextCredential, provider: nextDraft.provider })
        : {};
    return { nextErrors, nextCredentialErrors };
  };

  const saveDraft = async (event) => {
    event.preventDefault();
    const result = validateForm(draft, credential, true);
    if (Object.keys(result.nextErrors).length || Object.keys(result.nextCredentialErrors).length) {
      setErrors(result.nextErrors);
      setCredentialErrors(result.nextCredentialErrors);
      return;
    }
    setBusy("draft");
    try {
      await createOnPremLlmRuntimeDraft(buildDraftPayload(draft, credential));
      setCredential(emptyCredential);
      toast.success("LLM runtime draft saved.");
      await load();
    } catch (error) {
      toast.error(getOnPremErrorMessage(error, "LLM runtime draft could not be saved."));
    } finally {
      setBusy("");
    }
  };

  const openEdit = (profile) => {
    setEditDraft(profileToDraft(profile));
    setEditCredential(profileToCredential(profile));
    setEditErrors({});
    setEditCredentialErrors({});
    setDialog({ type: "edit", profile });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    const result = validateForm(editDraft, editCredential, false);
    if (Object.keys(result.nextErrors).length || Object.keys(result.nextCredentialErrors).length) {
      setEditErrors(result.nextErrors);
      setEditCredentialErrors(result.nextCredentialErrors);
      return;
    }
    setBusy(`edit-${dialog.profile.id}`);
    try {
      await patchOnPremLlmRuntimeDraft(dialog.profile.id, buildDraftPayload(editDraft, editCredential));
      toast.success("LLM runtime draft updated.");
      setDialog(null);
      await load();
    } catch (error) {
      toast.error(getOnPremErrorMessage(error, "LLM runtime draft could not be updated."));
    } finally {
      setBusy("");
    }
  };

  const openRotate = (profile) => {
    setRotateCredential(profileToCredential(profile));
    setRotateErrors({});
    setDialog({ type: "rotate", profile });
  };

  const rotateKey = async (event) => {
    event.preventDefault();
    const nextErrors = validateCredentialDraft({ ...rotateCredential, provider: dialog.profile.provider });
    if (Object.keys(nextErrors).length) {
      setRotateErrors(nextErrors);
      return;
    }
    const profileDraft = profileToDraft(dialog.profile);
    setBusy(`rotate-${dialog.profile.id}`);
    try {
      await replaceOnPremLlmRuntimeCredential(buildCredentialPayload({
        ...rotateCredential,
        mode: profileDraft.mode,
        provider: profileDraft.provider,
        serviceType: profileDraft.serviceType,
      }));
      toast.success("LLM runtime credential rotated.");
      setDialog(null);
      await load();
    } catch (error) {
      toast.error(getOnPremErrorMessage(error, "LLM runtime credential could not be rotated."));
    } finally {
      setBusy("");
    }
  };

  const activateDraft = async (profileId) => {
    setBusy(`activate-${profileId}`);
    try {
      await activateOnPremLlmRuntimeDraft(profileId);
      toast.success("LLM runtime draft activated.");
      await load();
    } catch (error) {
      toast.error(getOnPremErrorMessage(error, "LLM runtime draft could not be activated."));
    } finally {
      setBusy("");
    }
  };

  const deleteProfile = async () => {
    const profile = dialog.profile;
    const busyKey = `delete-${profile.id}`;
    setBusy(busyKey);
    try {
      if (profile.status === "draft") {
        await deleteOnPremLlmRuntimeDraft(profile.id);
      } else {
        await deleteOnPremLlmRuntimeProfile(profile.id);
      }
      toast.success("LLM runtime profile deleted.");
      setDialog(null);
      await load();
    } catch (error) {
      toast.error(getOnPremErrorMessage(error, "LLM runtime profile could not be deleted."));
    } finally {
      setBusy("");
    }
  };

  if (state.status === "loading") {
    return (
      <div className="onprem-loading">
        <Loader2 className="platform-spin" size={22} /> Loading LLM configuration...
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="onprem-state is-error" role="status">
        <AlertCircle size={22} />
        <div>
          <strong>Could not load LLM configuration</strong>
          <p>{state.message}</p>
        </div>
        <button type="button" className="onprem-button is-secondary" onClick={load}>
          <RefreshCcw size={15} /> Retry
        </button>
      </div>
    );
  }

  return (
    <section className="onprem-panel onprem-runtime-panel" role="tabpanel">
      <div className="onprem-section-head">
        <div>
          <h2>{heading}</h2>
          <p>{description}</p>
        </div>
        {headerMeta || (
          <span className={`onprem-status ${state.active ? "is-ready" : "is-incomplete"}`}>
            {state.active ? "Configured" : "Not configured"}
          </span>
        )}
      </div>

      <RuntimeStatus active={state.active} />

      <form className="onprem-runtime-form onprem-runtime-form--wide" onSubmit={saveDraft}>
        <div className="onprem-section-head onprem-section-head--compact">
          <div>
            <h2>New runtime config</h2>
            <p>Create a draft with credential details in one step.</p>
          </div>
        </div>
        <RuntimeFields
          draft={draft}
          credential={credential}
          errors={errors}
          credentialErrors={credentialErrors}
          options={state.options}
          localServices={state.localServices}
          localModels={localModels}
          localModelsStatus={localModelsStatus}
          onDraftChange={updateDraft}
          onCredentialChange={updateCredential}
          onModeChange={setMode}
        />
        <div className="onprem-actions">
          <button type="submit" className="onprem-button" disabled={busy === "draft"}>
            {busy === "draft" ? <Loader2 className="platform-spin" size={15} /> : <Save size={15} />}
            Save draft
          </button>
        </div>
      </form>

      <div className="onprem-runtime-drafts">
        <div>
          <h3>Runtime configs</h3>
          <p>Edit drafts, activate a draft, rotate the active key, or delete inactive history.</p>
        </div>
        {state.profiles.length ? (
          <div className="onprem-profile-table-wrap">
            <table className="onprem-profile-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>Mode</th>
                  <th>Version</th>
                  <th>Models</th>
                  <th>Credential</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.profiles.map((profile) => (
                  <tr key={profile.id}>
                    <td><span className={`onprem-profile-status is-${profile.status}`}>{profile.status}</span></td>
                    <td>{getProviderLabel(profile.provider, profile.server_type)}</td>
                    <td>{isLocalProfile(profile) ? "Local" : "Hosted"}</td>
                    <td>{profile.version ?? "N/A"}</td>
                    <td>{getProfileModels(profile)}</td>
                    <td>{getCredentialLabel(profile)}</td>
                    <td>
                      <div className="onprem-row-actions">
                        <button type="button" className="onprem-button is-secondary" onClick={() => setDialog({ type: "view", profile })}>
                          <Eye size={14} /> View
                        </button>
                        {profile.status === "draft" ? (
                          <>
                            <button type="button" className="onprem-button is-secondary" onClick={() => openEdit(profile)}>
                              <Pencil size={14} /> Edit
                            </button>
                            <button type="button" className="onprem-button is-secondary" aria-label={`Activate draft ${profile.id}`} onClick={() => activateDraft(profile.id)} disabled={busy === `activate-${profile.id}`}>
                              {busy === `activate-${profile.id}` ? <Loader2 className="platform-spin" size={14} /> : <Cpu size={14} />}
                              Activate
                            </button>
                            <button type="button" className="onprem-button is-danger" onClick={() => setDialog({ type: "delete", profile })}>
                              <Trash2 size={14} /> Delete
                            </button>
                          </>
                        ) : null}
                        {profile.status === "active" ? (
                          <button type="button" className="onprem-button is-secondary" onClick={() => openRotate(profile)}>
                            <KeyRound size={14} /> Rotate key
                          </button>
                        ) : null}
                        {profile.status === "inactive" ? (
                          <button type="button" className="onprem-button is-danger" onClick={() => setDialog({ type: "delete", profile })}>
                            <Trash2 size={14} /> Delete
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="onprem-muted">No runtime configs have been created yet.</p>
        )}
      </div>

      {footer ? <div className="onprem-runtime-footer">{footer}</div> : null}

      {dialog?.type === "view" ? (
        <RuntimeDialog title="Runtime profile" description="View provider, model, and credential metadata." onClose={() => setDialog(null)} actions={<button type="button" className="onprem-button" onClick={() => setDialog(null)}>Close</button>}>
          <ProfileDetails profile={dialog.profile} />
        </RuntimeDialog>
      ) : null}

      {dialog?.type === "edit" ? (
        <RuntimeDialog
          title="Edit runtime draft"
          description="Only draft profiles can be edited. Credential fields are saved with the draft when provided."
          onClose={() => setDialog(null)}
          actions={(
            <>
              <button type="button" className="onprem-button is-secondary" onClick={() => setDialog(null)}>Cancel</button>
              <button type="submit" form="onprem-edit-runtime-form" className="onprem-button" disabled={busy === `edit-${dialog.profile.id}`}>
                {busy === `edit-${dialog.profile.id}` ? <Loader2 className="platform-spin" size={15} /> : <Save size={15} />}
                Save changes
              </button>
            </>
          )}
        >
          <form id="onprem-edit-runtime-form" onSubmit={saveEdit}>
            <RuntimeFields
              draft={editDraft}
              credential={editCredential}
              errors={editErrors}
              credentialErrors={editCredentialErrors}
              options={state.options}
              localServices={state.localServices}
              localModels={localModels}
              localModelsStatus={localModelsStatus}
              onDraftChange={updateEditDraft}
              onCredentialChange={updateEditCredential}
              onModeChange={setEditMode}
              idPrefix="edit-runtime"
            />
          </form>
        </RuntimeDialog>
      ) : null}

      {dialog?.type === "rotate" ? (
        <RuntimeDialog
          title="Rotate credential"
          description="Runtime settings are locked for the active config. Only key details can be changed."
          onClose={() => setDialog(null)}
          actions={(
            <>
              <button type="button" className="onprem-button is-secondary" onClick={() => setDialog(null)}>Cancel</button>
              <button type="submit" form="onprem-rotate-runtime-form" className="onprem-button" disabled={busy === `rotate-${dialog.profile.id}`}>
                {busy === `rotate-${dialog.profile.id}` ? <Loader2 className="platform-spin" size={15} /> : <KeyRound size={15} />}
                Rotate key
              </button>
            </>
          )}
        >
          <ProfileDetails profile={dialog.profile} />
          <form id="onprem-rotate-runtime-form" className="onprem-rotate-form" onSubmit={rotateKey}>
            <label className="onprem-field" htmlFor="rotate-display-name">
              <span>Credential display name</span>
              <input id="rotate-display-name" aria-label="Credential display name" value={rotateCredential.displayName} onChange={updateRotateCredential("displayName")} placeholder="Rotated provider key" />
            </label>
            <label className="onprem-field" htmlFor="rotate-api-key">
              <span>New API key<Required /></span>
              <input id="rotate-api-key" aria-label="New API key" type="password" value={rotateCredential.apiKey} onChange={updateRotateCredential("apiKey")} placeholder="Enter new API key" />
              <FieldError>{rotateErrors.apiKey}</FieldError>
            </label>
          </form>
        </RuntimeDialog>
      ) : null}

      {dialog?.type === "delete" ? (
        <RuntimeDialog
          title="Delete runtime config"
          description={`Delete ${getProviderLabel(dialog.profile.provider, dialog.profile.server_type)} ${dialog.profile.status} profile version ${dialog.profile.version ?? dialog.profile.id}?`}
          onClose={() => setDialog(null)}
          actions={(
            <>
              <button type="button" className="onprem-button is-secondary" onClick={() => setDialog(null)}>Cancel</button>
              <button type="button" className="onprem-button is-danger" onClick={deleteProfile} disabled={busy === `delete-${dialog.profile.id}`}>
                {busy === `delete-${dialog.profile.id}` ? <Loader2 className="platform-spin" size={15} /> : <Trash2 size={15} />}
                Delete
              </button>
            </>
          )}
        >
          <ProfileDetails profile={dialog.profile} />
        </RuntimeDialog>
      ) : null}
    </section>
  );
}
